import { generatePath, matchRoutes } from 'react-router-dom';

import { organizationQueryKey } from '@cast/constants';

import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { LIST_PRICE_QUERY_PARAM } from 'features/cost-report/constants';

// prettier-ignore
const availabilityMatrix = [
  /*                      clusters | allocation groups | allocation group | node templates */
  /* compute cost */    [ true, true, true, true],
  /* network cost */    [ false, false, false, false],
  /* efficiency cost */ [ true, false, false, false],
  /* gpu utilization */ [ false, false, false, false],
];

const BASE_URL_OP = `${PRODUCT_BASE_PATHS.automation}/cost-report`;
const PERSISTENT_QUERY_PARAMS = [
  organizationQueryKey,
  'cost_report_date_preset',
  'cost_report_from',
  'cost_report_to',
  'cost_report_timezone',
  LIST_PRICE_QUERY_PARAM,
];

// Note: order is important when matching routes and checking availability by index, make sure "details" segments are defined before "list" segments
const routeSegmentsByObject = {
  clusters: 'clusters',
  allocationGroups: 'allocation-groups',
  allocationGroup: 'allocation-groups/__report_type__/:allocationGroup',
  nodeTemplates: 'node-templates',
} as const;

// Note: order is important when checking availability by index
const routeSegmentsByReportingType = {
  computeCost: 'compute-cost',
  networkCost: 'network-cost',
  efficiency: 'efficiency',
  gpuUtilization: 'gpu-utilization',
} as const;

type ObjectSegmentKeys = keyof typeof routeSegmentsByObject;
type ReportTypeSegmentKeys = keyof typeof routeSegmentsByReportingType;
export type OrgCostMonitoringRouteKeys =
  | ObjectSegmentKeys
  | `${ObjectSegmentKeys}.${ReportTypeSegmentKeys}`;

type NavigationParams = {
  object?: ObjectSegmentKeys;
  reportType?: ReportTypeSegmentKeys;
};

type RouteMatch = {
  object: ObjectSegmentKeys;
  reportType: ReportTypeSegmentKeys;
  path: string;
  params: Record<string, string>;
};

export type AllocationGroup = 'allocationGroup';

export type LinkParams<T extends string> = Partial<Record<T, string>>;

const isAvailableRoute = (o: ObjectSegmentKeys, t: ReportTypeSegmentKeys) => {
  return availabilityMatrix[
    Object.keys(routeSegmentsByReportingType).indexOf(t)
  ][Object.keys(routeSegmentsByObject).indexOf(o)];
};

const ORG_COST_MONITORING_PATHS = ((): Record<
  OrgCostMonitoringRouteKeys,
  string
> => {
  const r = {} as Record<OrgCostMonitoringRouteKeys, string>;

  const toRouteUrl = (pathSegment: string) => {
    return `${BASE_URL_OP}/${pathSegment}`;
  };

  // prettier-ignore
  (Object.keys(routeSegmentsByObject) as ObjectSegmentKeys[]).forEach(
    (object) => {
      const objectSegment = routeSegmentsByObject[object];
      if (!objectSegment.includes('__report_type__')) {
        r[object] = toRouteUrl(routeSegmentsByObject[object]);
      }

      (
        Object.keys(routeSegmentsByReportingType) as ReportTypeSegmentKeys[]
      ).forEach((reportingType) => {
        if (isAvailableRoute(object, reportingType)) {
          const key: OrgCostMonitoringRouteKeys = `${object}.${reportingType}`;
          const reportingTypeSegment =
            routeSegmentsByReportingType[reportingType];
          if (objectSegment.includes('__report_type__')) {
            r[key] = toRouteUrl(routeSegmentsByObject[object].replace(
              '__report_type__',
              reportingTypeSegment
            ));
          } else {
            r[key] = toRouteUrl(`${routeSegmentsByObject[object]}/${routeSegmentsByReportingType[reportingType]}`);
          }
        }
      });
    }
  );

  return r;
})();

export const getOrgCostMonitoringRoutePath = (
  key: OrgCostMonitoringRouteKeys
): string => ORG_COST_MONITORING_PATHS[key] ?? '/404';

const retrieveCurrentRouteContext = (): RouteMatch | undefined => {
  return (
    Object.keys(ORG_COST_MONITORING_PATHS) as OrgCostMonitoringRouteKeys[]
  ).reduce((acc, k) => {
    const match = matchRoutes(
      [{ path: ORG_COST_MONITORING_PATHS[k] }],
      window.location.pathname
    );

    if (match) {
      const [object, reportType] = k.split('.') as [
        ObjectSegmentKeys,
        ReportTypeSegmentKeys
      ];

      return {
        object,
        reportType,
        path: ORG_COST_MONITORING_PATHS[k],
        params: match[0].params,
      } as RouteMatch;
    }

    return acc;
  }, undefined as RouteMatch | undefined);
};

const appendQueryParams = (path: string, queryParameters: URLSearchParams) => {
  const mergerParams = new URLSearchParams();
  queryParameters.forEach((value, key) => {
    mergerParams.append(key, value);
  });

  const searchParams = new URLSearchParams(window.location.search);
  // Copy query parameters from the current URL
  searchParams.forEach((value, key) => {
    if (PERSISTENT_QUERY_PARAMS.includes(key)) {
      mergerParams.append(key, value);
    }
  });

  return `${path}?${mergerParams.toString()}`;
};

export const buildLinkToReport = (
  destinationPath: string,
  params: Record<string, string> = {},
  queryParams: URLSearchParams = new URLSearchParams()
) => {
  // retrieve more data from the current URL
  const match = retrieveCurrentRouteContext();
  const mergedParams: Record<string, string | undefined> = {
    ...match?.params,
    ...params,
  };

  const path = generatePath(destinationPath, mergedParams);

  // Validate whether all params are provided
  const missingParams = path.match(/\/:\w+/g);
  if (missingParams?.length) {
    throw new Error(
      `Missing params ${missingParams
        .join(', ')
        .replaceAll('/:', '')} for route ${path}`
    );
  }

  return appendQueryParams(path, queryParams);
};

export const getLinkToReport = (
  { object, reportType }: NavigationParams,
  params: Record<string, string> = {},
  queryParams: URLSearchParams = new URLSearchParams()
) => {
  // retrieve even more data from the current URL
  const match = retrieveCurrentRouteContext();

  // building navigation key by importance weight: given params => existing context => default
  const _object: ObjectSegmentKeys = object ?? match?.object ?? 'clusters';
  const _reportType: ReportTypeSegmentKeys =
    reportType ?? match?.reportType ?? 'computeCost';

  const key: OrgCostMonitoringRouteKeys = isAvailableRoute(_object, _reportType)
    ? `${_object}.${_reportType}`
    : `${_object}.computeCost`;

  const destinationPath = getOrgCostMonitoringRoutePath(key);
  return buildLinkToReport(destinationPath, params, queryParams);
};
